// Client-error telemetry (beta): forward the first few console errors + uncaught errors to the
// world server (→ server logs), so a player's broken session leaves evidence we can read instead
// of depending on screenshots. The duel's defensive guards all console.error rather than throw —
// intercepting console.error IS the signal path. Prod builds only (dev has a human at the console);
// capped + deduped so a repeating guard can never flood the wire (server hard-caps too).
import { send, net } from "./stores/net.svelte.ts";

const MAX_LINES = 12;
let sent = 0;
const seen = new Set<string>();

function forward(text: string): void {
  if (sent >= MAX_LINES || !net.connected) return;
  const line = text.slice(0, 560);
  const key = line.slice(0, 120); // dedupe on the head — repeated guard logs differ only in tails
  if (seen.has(key)) return;
  seen.add(key);
  sent++;
  try { send({ t: "ClientLog", text: line }); } catch { /* telemetry must never break the app */ }
}

const fmt = (a: unknown): string =>
  a instanceof Error ? `${a.message}\n${(a.stack ?? "").split("\n").slice(1, 4).join("\n")}` :
  typeof a === "string" ? a : (() => { try { return JSON.stringify(a); } catch { return String(a); } })();

/** Install once at boot (prod builds). */
export function initTelemetry(): void {
  if (import.meta.env.DEV) return;
  const orig = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    orig(...args);
    forward(args.map(fmt).join(" "));
  };
  window.addEventListener("error", (e) => forward(`uncaught: ${e.message} @ ${e.filename}:${e.lineno}`));
  window.addEventListener("unhandledrejection", (e) => forward(`unhandled rejection: ${fmt(e.reason)}`));
}
